import React, { useEffect, useState } from "react";

// 안전장구(PPE) 알림 ON/OFF 설정 섹션.
// 서버 AI가 카메라 영상을 분석해 미착용을 감지한다(폰 온디바이스 아님).
// 서버 1대 = 현장 1곳이므로 항목 옆 토글 스위치로 켜고 끈다.
// 끄면 그 항목은 서버 AI가 감지해도 서버가 걸러서 관제·푸시 알림을 보내지 않는다.
// (관리자 페이지 안에 끼워 넣어 쓰는 재사용 섹션)
export default function PpeAlertSettings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingType, setSavingType] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [savingVoice, setSavingVoice] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/admin/ppe-site-config", {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setErr(data.message || data.error || "설정을 불러오지 못했습니다.");
        setItems([]);
        return;
      }
      setItems(data.items || []);
      setVoiceEnabled(data.voiceEnabled !== false);
    } catch (e) {
      setErr("설정을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // 토글 즉시 저장(낙관적 업데이트, 실패 시 원복).
  const handleToggle = async (item) => {
    const next = !item.alertEnabled;
    setSavingType(item.type);
    setItems((prev) =>
      prev.map((it) =>
        it.type === item.type ? { ...it, alertEnabled: next } : it
      )
    );
    try {
      const res = await fetch("/api/admin/ppe-site-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ violationType: item.type, alertEnabled: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setItems((prev) =>
          prev.map((it) =>
            it.type === item.type ? { ...it, alertEnabled: !next } : it
          )
        );
        alert(data.message || data.error || "저장에 실패했습니다.");
      }
    } catch (e) {
      setItems((prev) =>
        prev.map((it) =>
          it.type === item.type ? { ...it, alertEnabled: !next } : it
        )
      );
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setSavingType(null);
    }
  };

  // 음성 안내 전역 토글(낙관적 업데이트, 실패 시 원복).
  const handleVoiceToggle = async () => {
    const next = !voiceEnabled;
    setSavingVoice(true);
    setVoiceEnabled(next);
    try {
      const res = await fetch("/api/admin/ppe-site-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ violationType: "VOICE_ALERT", alertEnabled: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setVoiceEnabled(!next);
        alert(data.message || data.error || "저장에 실패했습니다.");
      }
    } catch (e) {
      setVoiceEnabled(!next);
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setSavingVoice(false);
    }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
      <div className="mb-1 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">
            안전장구 알림 설정
          </h3>
          <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            서버 AI 영상분석 기반
          </span>
        </div>
        <button
          type="button"
          onClick={loadSettings}
          className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
        >
          새로고침
        </button>
      </div>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        <b>서버 AI</b>가 미착용을 감지합니다. <b>끄면</b> 그 항목은 알림을 보내지 않아요. (토글 즉시 적용)
      </p>

      {/* 음성 안내 전역 토글 — 현장 단말(민트캠)에서 미착용 음성 읽어주기 */}
      {!loading && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 dark:border-emerald-700 dark:bg-emerald-900/20">
          <div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">
              미착용 음성 안내
            </div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {voiceEnabled
                ? "현장 단말(민트캠)에서 “○○를 착용하세요” 음성을 읽어줍니다"
                : "음성 안내를 보내지 않습니다 (관제·푸시 알림은 위 항목 설정대로 유지)"}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={voiceEnabled}
            disabled={savingVoice}
            onClick={handleVoiceToggle}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition disabled:opacity-50 ${
              voiceEnabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                voiceEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      )}

      {err && (
        <div className="mb-3 rounded-xl bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {err}
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">
          불러오는 중...
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">
          표시할 항목이 없습니다.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {items.map((item) => {
            const on = !!item.alertEnabled;
            const busy = savingType === item.type;
            return (
              <div
                key={item.type}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-700"
              >
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white">
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {on ? "미착용 시 알림 보냄" : "알림 보내지 않음"}
                  </div>
                </div>

                {/* 토글 스위치 */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  disabled={busy}
                  onClick={() => handleToggle(item)}
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition disabled:opacity-50 ${
                    on ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      on ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
